/**
 * Service for handling URL query parameters
 * Provides reusable utilities for syncing query parameters with URL state
 */

/**
 * Parse URL query parameters into an object
 * @param {string} searchString - The location.search string (e.g., "?name=test&id=1")
 * @param {Object} schema - Schema defining which parameters to extract and their types
 * Example schema: { name: 'string', id: 'number', active: 'boolean' }
 * @returns {Object} Parsed parameters object
 */
export const parseQueryParams = (searchString, schema) => {
  const params = new URLSearchParams(searchString);
  const result = {};

  Object.entries(schema).forEach(([key, type]) => {
    const value = params.get(key);

    if (value !== null) {
      if (type === "number") {
        result[key] = Number(value);
      } else if (type === "boolean") {
        result[key] = value === "true";
      } else {
        result[key] = value;
      }
    } else {
      result[key] = null;
    }
  });

  return result;
};

/**
 * Convert query parameters object to URL search string
 * @param {Object} queryParams - The parameters object
 * @returns {string} URL search string (e.g., "?name=test&id=1")
 */
export const buildQueryString = (queryParams) => {
  const params = new URLSearchParams();

  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      params.append(key, value);
    }
  });

  return params.toString();
};

/**
 * Clear specific or all query parameters
 * @param {Object} queryParams - Current parameters object
 * @param {string[]} keysToReset - Optional array of specific keys to reset. If not provided, all are reset.
 * @returns {Object} New parameters object with specified keys reset to null
 */
export const resetQueryParams = (queryParams, keysToReset = null) => {
  if (!keysToReset) {
    // Reset all parameters
    const reset = {};
    Object.keys(queryParams).forEach((key) => {
      reset[key] = null;
    });
    return reset;
  }

  // Reset specific parameters
  const reset = { ...queryParams };
  keysToReset.forEach((key) => {
    reset[key] = null;
  });
  return reset;
};

/**
 * Update a single query parameter
 * @param {Object} queryParams - Current parameters object
 * @param {string} key - Parameter key to update
 * @param {*} value - New value
 * @returns {Object} Updated parameters object
 */
export const updateQueryParam = (queryParams, key, value) => {
  return {
    ...queryParams,
    [key]: value,
  };
};
